import { useMutation, useQuery } from '@apollo/client'
import {
  deserializePin,
  getCurrentEncryptionVersion,
  parsePinToParts,
  prehashPassword,
  processVersionMigration,
  savePersonalKeys,
} from '@faceup/crypto'
import { useContext, useState } from 'react'
import { graphql } from '../../__generated__'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { UserContext } from '../../Contexts/UserContext'
import { usePreLogin } from '../../hooks/usePreLogin'
import useAnalytics from '../../utils/analytics'
import useAuth from '../../utils/auth'

const mutations = {
  VictimLogin: graphql(`
    mutation VictimLoginMutation($input: VictimLoginInput!) {
      victimLogin(input: $input) {
        token
        publicKey
        privateKey
        nonce
      }
    }
  `),
  MigrateVictimEncryption: graphql(`
    mutation MigrateVictimEncryptionMutation(
      $input: MigrateVictimEncryptionInput!
    ) {
      migrateVictimEncryption(input: $input) {
        isValid
      }
    }
  `),
}

const query = graphql(`
  query SystemInfoCheckReportQuery {
    systemInfo {
      id
      publicKey
    }
  }
`)

type UseCheckReportLoginProps = {
  onSuccess: () => void
}

export const useCheckReportLogin = ({ onSuccess }: UseCheckReportLoginProps) => {
  const { setJwt } = useAuth()
  const { performPreLogin, loading: loadingPreLogin } = usePreLogin()
  const { client } = useContext(LayoutContext)
  const { reportSource } = useContext(UserContext)
  const { track } = useAnalytics()
  const [pinInput, setPinInput] = useState({ value: '', error: false })

  const systemInfo = useQuery(query)?.data?.systemInfo

  const [loginReport, { loading: loadingReport }] = useMutation(mutations.VictimLogin, {
    onError: ({ message }) => {
      if (message === 'Invalid login credentials') {
        setPinInput(prev => ({ ...prev, error: true }))
      }
    },
  })

  const [migrateVictimEncryption, { loading: loadingMigration }] = useMutation(
    mutations.MigrateVictimEncryption
  )

  const loading = loadingReport || loadingPreLogin || loadingMigration

  const isApp = client === 'app'

  const onSubmit = async () => {
    if (pinInput.value.trim().length < 8) {
      setPinInput(prev => ({ ...prev, error: true }))
      return
    }

    const { passwordKey, passwordKeyPrehash } = await performPreLogin(pinInput.value)
    const pin = deserializePin(pinInput.value)
    const { identity, version, password } = parsePinToParts(pin)

    const loginResponse = await loginReport({
      variables: {
        input: {
          identity,
          passwordPrehash: passwordKeyPrehash,
          version,
          reportPassword: reportSource?.password,
          rememberMe: false,
          organizationalUnitId: reportSource?.institutionId ?? '',
        },
      },
    })

    if (!loginResponse.data) {
      setPinInput(prev => ({ ...prev, error: true }))
      return
    }

    await setJwt(loginResponse?.data?.victimLogin?.token ?? '')

    const login = loginResponse?.data?.victimLogin
    if (passwordKey && version === getCurrentEncryptionVersion()) {
      await savePersonalKeys({
        publicKey: login?.publicKey ?? '',
        privateKey: login?.privateKey ?? '',
        nonce: login?.nonce ?? '',
        passwordKey,
        rememberMe: isApp,
        version,
      })
    } else {
      const result = await processVersionMigration({
        password,
        systemPublicKey: systemInfo?.publicKey ?? '',
        privateKey: login?.privateKey ?? '',
        publicKey: login?.publicKey ?? '',
        version,
      })

      if (result.isErr()) {
        return
      }

      const {
        newNonce,
        newPasswordPrehash,
        newPrivateKeyEncrypted,
        newSalt,
        currentPasswordPrehash,
      } = result.value

      await migrateVictimEncryption({
        variables: {
          input: {
            newNonce,
            newPasswordPrehash,
            newPrivateKeyEncrypted,
            newSalt,
            currentPasswordPrehash,
          },
        },
      })

      const prehashedPassword = await prehashPassword({
        password,
        salt: newSalt,
      })

      if (prehashedPassword.isErr()) {
        return
      }

      const { passwordKey: newPasswordKey } = prehashedPassword.value

      await savePersonalKeys({
        publicKey: login?.publicKey ?? '',
        privateKey: newPrivateKeyEncrypted,
        nonce: newNonce,
        passwordKey: newPasswordKey,
        rememberMe: isApp,
        version: getCurrentEncryptionVersion(),
      })
    }

    setPinInput({ value: '', error: false })
    onSuccess()
    track('correct_pin_entered')
  }

  return {
    pinInput,
    setPinInput,
    loading,
    onSubmit,
  }
}
